import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client/react';
import { Chip, IconButton, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { Add, Close, Search } from '@mui/icons-material';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import { nbNO } from '@mui/x-data-grid-pro/locales';
import { MainContentPanel } from '../components/MainContentPanel';
import { IdentityProviderSummaryItemFragment } from '../__generated__/graphql';
import { ALL_IDENTITY_PROVIDERS } from './Sso.graphql';
import { AddADFSConfigDialog } from '../administration/ADFSConfig/AddADFSConfigDialog';

type Row = IdentityProviderSummaryItemFragment;

const SYNC_STATES = [
  { label: 'Av', color: 'default' },
  { label: 'Mangler token', color: 'warning' },
  { label: 'På', color: 'info' },
  { label: 'Katalogen styrer', color: 'success' },
] as const;

const syncState = (row: Row) => SYNC_STATES[!row.scimEnabled ? 0 : !row.scimHasActiveToken ? 1 : row.scimAuthoritative ? 3 : 2];

const matches = (row: Row, term: string) => {
  const needle = term.toLowerCase();
  return [row.companyName, row.scheme, ...(row.emailDomains ?? [])].some((x) => x?.toLowerCase().includes(needle));
};

export default function IdentityProviderOverview() {
  const { data, loading } = useQuery(ALL_IDENTITY_PROVIDERS, { fetchPolicy: 'cache-and-network' });
  const [searchTerm, setSearchTerm] = useState('');
  const [adding, setAdding] = useState(false);
  const navigate = useNavigate();

  const rows = useMemo(() => {
    const all = (data?.providers ?? []).filter((x): x is Row => !!x);
    const term = searchTerm.trim();
    return term ? all.filter((x) => matches(x, term)) : all;
  }, [data, searchTerm]);

  const columns = useMemo<GridColDef<Row>[]>(
    () => [
      {
        field: 'scheme',
        headerName: 'Identitetsleverandør',
        flex: 0.8,
        minWidth: 140,
        renderCell: ({ row }) => <Link to={`/license/${row.bksID}/idp`}>{row.scheme}</Link>,
      },
      {
        field: 'companyName',
        headerName: 'Kunde',
        flex: 1.2,
        minWidth: 200,
        renderCell: ({ row }) => <Link to={`/license/${row.bksID}`}>{row.companyName ?? `Lisens ${row.bksID}`}</Link>,
      },
      {
        field: 'emailDomains',
        headerName: 'Domener',
        flex: 1,
        minWidth: 160,
        valueGetter: (_value, row) => (row.emailDomains ?? []).join(', '),
      },
      {
        field: 'seatsInUse',
        headerName: 'Seter',
        width: 110,
        valueGetter: (_value, row) => row.seatsInUse,
        renderCell: ({ row }) => `${row.seatsInUse} / ${row.seatsBought}`,
      },
      {
        field: 'isRestricted',
        headerName: 'Begrensning',
        display: 'flex',
        width: 150,
        valueGetter: (_value, row) => (row.isRestricted ? `Ja, +${row.extraUserPercentage} %` : 'Nei'),
        renderCell: ({ row }) =>
          row.isRestricted ? (
            <Typography variant="body2" color={row.hasRoomForNewUser ? undefined : 'error.main'}>
              Ja, +{row.extraUserPercentage} %
            </Typography>
          ) : (
            'Nei'
          ),
      },
      {
        field: 'scimEnabled',
        headerName: 'Synkronisering',
        display: 'flex',
        width: 190,
        type: 'singleSelect',
        valueOptions: SYNC_STATES.map((x) => x.label),
        valueGetter: (_value, row) => syncState(row).label,
        renderCell: ({ row }) => <Chip size="small" variant="outlined" color={syncState(row).color} label={syncState(row).label} />,
      },
    ],
    [],
  );

  return (
    <MainContentPanel>
      <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
        Firmapålogging
        <IconButton onClick={() => setAdding(true)} title="Ny firmapålogging" aria-label="Ny firmapålogging" size="large">
          <Add />
        </IconButton>
      </Typography>
      <Stack spacing={2}>
        <TextField
          placeholder="Søk..."
          variant="standard"
          fullWidth
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          slotProps={{
            htmlInput: { 'aria-label': 'Søk etter kunde, identitetsleverandør eller domene' },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" aria-label="Tøm søket" onClick={() => setSearchTerm('')} edge="end">
                    <Close fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
        <DataGridPro
          autoHeight
          density="compact"
          loading={loading && !data}
          rows={rows}
          columns={columns}
          getRowId={(row) => row.bksID}
          localeText={nbNO.components.MuiDataGrid.defaultProps.localeText}
          pagination
          pageSizeOptions={[25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 50, page: 0 } },
            sorting: { sortModel: [{ field: 'scheme', sort: 'asc' }] },
          }}
          sx={{ '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 } }}
        />
      </Stack>
      {adding && (
        <AddADFSConfigDialog
          takenLicenseIds={new Set((data?.providers ?? []).map((x) => x!.bksID))}
          onClose={() => setAdding(false)}
          onCreated={(bksID) => navigate(`/license/${bksID}/idp`)}
        />
      )}
    </MainContentPanel>
  );
}
